import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    Calculator,
    Trash2,
    Plus,
    Minus,
    Tag,
    Check,
    AlertCircle,
    Loader2,
    Download,
    Mail,
    FileText,
    ArrowLeft,
    ArrowRight,
    Sparkles,
    CalendarIcon,
    ZoomIn,
    X,
} from "lucide-react";
import {
    validateDiscount,
    submitQuote,
    saveQuoteDraft,
} from "../lib/api";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format, differenceInCalendarDays, addDays, startOfDay } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { ServicesManager } from "./ServicesManager";
import QuoteItemDetail from "./QuoteItemDetail";

const VAT_RATE = 22;
const DRAFT_KEY = "nsp_quote_draft_v1";

const loadDraft = () => {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

const formatEuro = (v) =>
    `€ ${(v || 0).toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/* Smart pricing client-side (identico al backend pricing.py).
   Trova la combinazione mesi+settimane+giorni che minimizza il costo. */
const computeSmartPrice = (days, daily = 0, weekly = 0, monthly = 0) => {
    days = Math.max(1, Math.min(365, parseInt(days, 10) || 1));
    daily = Math.max(0, daily || 0);
    weekly = Math.max(0, weekly || 0);
    monthly = Math.max(0, monthly || 0);

    let best = null;
    const maxM = monthly > 0 ? Math.floor(days / 30) : 0;
    for (let m = 0; m <= maxM; m++) {
        const remM = days - m * 30;
        const maxW = weekly > 0 ? Math.floor(remM / 7) : 0;
        for (let w = 0; w <= maxW; w++) {
            const d = remM - w * 7;
            if (d < 0) continue;
            if (d > 0 && daily <= 0) {
                // promote: try +1 week
                if (weekly > 0) {
                    const t = m * monthly + (w + 1) * weekly;
                    if (best === null || t < best.unit_total)
                        best = { months: m, weeks: w + 1, extra_days: 0, unit_total: t };
                    continue;
                }
                // or +1 month
                const t = (m + 1) * monthly + w * weekly;
                if (best === null || t < best.unit_total)
                    best = { months: m + 1, weeks: w, extra_days: 0, unit_total: t };
                continue;
            }
            const total = m * monthly + w * weekly + d * daily;
            if (best === null || total < best.unit_total) {
                best = { months: m, weeks: w, extra_days: d, unit_total: total };
            }
        }
    }
    if (!best) {
        best = { months: 0, weeks: 0, extra_days: days, unit_total: days * (daily || 0) };
    }
    return { ...best, unit_total: Math.round(best.unit_total * 100) / 100 };
};

const buildDurationLabel = (months, weeks, extra_days) => {
    const parts = [];
    if (months > 0) parts.push(`${months} ${months === 1 ? "mese" : "mesi"}`);
    if (weeks > 0) parts.push(`${weeks} ${weeks === 1 ? "settimana" : "settimane"}`);
    if (extra_days > 0) parts.push(`${extra_days} ${extra_days === 1 ? "giorno" : "giorni"}`);
    return parts.length ? parts.join(" + ") : "1 giorno";
};

const buildBreakdownLabel = (months, weeks, extra_days, daily, weekly, monthly) => {
    const parts = [];
    if (months > 0)
        parts.push(`${months} × ${formatEuro(monthly)}/mese`);
    if (weeks > 0)
        parts.push(`${weeks} × ${formatEuro(weekly)}/sett.`);
    if (extra_days > 0)
        parts.push(`${extra_days} × ${formatEuro(daily)}/gg`);
    return parts.join(" + ");
};

export const QuoteCalculator = ({ items, setItems }) => {
    const [step, setStep] = useState(1);
    const panelRef = useRef(null);
    const firstStepRender = useRef(true);

    // Ergonomia: a ogni cambio passaggio riporta la vista all'inizio del pannello
    useEffect(() => {
        if (firstStepRender.current) {
            firstStepRender.current = false;
            return;
        }
        requestAnimationFrame(() => {
            if (!panelRef.current) return;
            const y = panelRef.current.getBoundingClientRect().top + window.scrollY - 96;
            window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
        });
    }, [step]);
    const [discountCode, setDiscountCode] = useState(() => loadDraft()?.discountCode || "");
    const [discountInfo, setDiscountInfo] = useState(null);
    const [validating, setValidating] = useState(false);
    const [customer, setCustomer] = useState(() => ({
        name: "",
        email: "",
        phone: "",
        company: "",
        project: "",
        ...(loadDraft()?.customer || {}),
    }));
    const [consentContact, setConsentContact] = useState(false);
    const [consentPrivacy, setConsentPrivacy] = useState(false);
    const [website, setWebsite] = useState(""); // honeypot
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [welcomeName] = useState(() => {
        const n = (loadDraft()?.customer?.name || "").trim();
        if (n.length < 2) return "";
        const first = n.split(/\s+/)[0];
        return first.charAt(0).toUpperCase() + first.slice(1);
    });

    useEffect(() => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({ customer, discountCode }));
        } catch (_) {
            /* ignore */
        }
    }, [customer, discountCode]);

    // Recupero preventivo: salva la bozza sul server (email valida + articoli presenti)
    useEffect(() => {
        if (result) return;
        if (!/^\S+@\S+\.\S+$/.test(customer.email) || items.length === 0) return;
        const t = setTimeout(() => {
            saveQuoteDraft({
                email: customer.email,
                name: customer.name,
                phone: customer.phone,
                items: items.map((it) => ({
                    product_id: it.product.id,
                    quantity: it.quantity || 1,
                    days: it.days || 1,
                    pickup_date: it.pickup_date || null,
                    return_date: it.return_date || null,
                })),
            }).catch(() => {});
        }, 2000);
        return () => clearTimeout(t);
    }, [customer, items, result]);

    const updateItem = (id, patch) => {
        setItems((prev) =>
            prev.map((it) => (it.product.id === id ? { ...it, ...patch } : it))
        );
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((it) => it.product.id !== id));
    };

    const subtotal = useMemo(() => {
        return items.reduce((sum, it) => {
            const pr = computeSmartPrice(
                it.days,
                it.product.daily || 0,
                it.product.weekly || 0,
                it.product.monthly || 0
            );
            return sum + pr.unit_total * it.quantity;
        }, 0);
    }, [items]);

    const discountAmount = discountInfo?.valid
        ? Math.round((subtotal * discountInfo.percentage) / 100 * 100) / 100
        : 0;
    const taxable = Math.max(0, subtotal - discountAmount);
    const vatAmount = Math.round(taxable * VAT_RATE) / 100;
    const total = taxable;                              // IVA esclusa (compatibilità nomi esistenti)
    const totalWithVat = Math.round((taxable + vatAmount) * 100) / 100;

    const handleValidateDiscount = async () => {
        if (!discountCode.trim()) return;
        setValidating(true);
        try {
            const res = await validateDiscount(discountCode.trim());
            if (res.valid) {
                setDiscountInfo(res);
                toast.success(`Sconto ${res.code} applicato (-${res.percentage}%)`);
            } else {
                setDiscountInfo(null);
                toast.error("Codice sconto non valido");
            }
        } catch (e) {
            toast.error("Errore di validazione codice");
        } finally {
            setValidating(false);
        }
    };

    const canGoStep2 = items.length > 0;
    const canGoStep3 =
        canGoStep2 &&
        items.every(
            (it) =>
                it.days >= 1 &&
                it.quantity >= 1 &&
                it.pickup_date &&
                it.return_date
        );
    const canSubmit =
        canGoStep3 &&
        customer.name.length >= 2 &&
        /^\S+@\S+\.\S+$/.test(customer.email) &&
        customer.phone.length >= 5 &&
        consentPrivacy;

    const handleSubmit = async () => {
        if (!canSubmit) {
            toast.error("Compila tutti i campi obbligatori e accetta l'informativa.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                customer,
                items: items.map((it) => ({
                    product_id: it.product.id,
                    days: it.days,
                    quantity: it.quantity,
                    pickup_date: it.pickup_date,
                    return_date: it.return_date,
                    services: it.services || [],
                })),
                discount_code: discountInfo?.valid ? discountInfo.code : null,
                consent_contact: consentContact,
                consent_privacy: consentPrivacy,
                website,
            };
            const res = await submitQuote(payload);
            setResult(res);
            setStep(5);
            try {
                localStorage.removeItem(DRAFT_KEY);
            } catch (_) {
                /* ignore */
            }
            toast.success(`Preventivo ${res.quote_number} creato!`);
        } catch (e) {
            const msg =
                e?.response?.data?.detail || "Errore nell'invio del preventivo";
            toast.error(typeof msg === "string" ? msg : "Errore invio preventivo");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="preventivo"
            data-testid="quote-calculator-section"
            className="relative bg-gradient-to-br from-white via-white to-nsp-green-50 py-16 sm:py-20 lg:py-28 overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative">
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
                    {/* LEFT: Header + Steps */}
                    <div className="lg:col-span-4 lg:sticky lg:top-28 self-start min-w-0">
                        <div className="text-xs font-bold uppercase tracking-[0.3em] text-nsp-green mb-6 flex items-center gap-3">
                            <Calculator className="w-4 h-4" /> Calcolatore Preventivo
                        </div>
                        <h2
                            data-testid="quote-title"
                            className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-nsp-green tracking-tighter"
                        >
                            Costruisci
                            <br />
                            il tuo
                            <br />
                            <span className="text-nsp-yellow-dark">preventivo.</span>
                        </h2>
                        <p className="text-gray-600 mt-6 leading-relaxed text-base">
                            Seleziona i mezzi, scegli la durata, applica il tuo codice
                            sconto e ricevi il PDF dettagliato in pochi click.
                        </p>

                        <div className="mt-10 space-y-1">
                            {[
                                { n: 1, label: "Attrezzature" },
                                { n: 2, label: "Date & sconto" },
                                { n: 3, label: "I tuoi dati" },
                                { n: 4, label: "Conferma & invio" },
                            ].map((s) => (
                                <div
                                    key={s.n}
                                    data-testid={`quote-step-indicator-${s.n}`}
                                    className={`flex items-center gap-4 py-3 border-l-2 pl-4 transition-colors ${
                                        step === s.n
                                            ? "border-nsp-yellow-dark text-nsp-green"
                                            : step > s.n
                                              ? "border-nsp-green/40 text-gray-500"
                                              : "border-gray-200 text-gray-300"
                                    }`}
                                >
                                    <span className="font-display text-2xl leading-none w-8">
                                        {String(s.n).padStart(2, "0")}
                                    </span>
                                    <span className="text-sm font-bold uppercase tracking-wider">
                                        {s.label}
                                    </span>
                                    {step > s.n && (
                                        <Check className="w-4 h-4 ml-auto text-nsp-green-vivid" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: Panel */}
                    <div className="lg:col-span-8 min-w-0" ref={panelRef}>
                        {welcomeName && !result && (
                            <div
                                data-testid="quote-welcome-back"
                                className="mb-5 flex items-center gap-3.5 bg-nsp-green text-white rounded-2xl px-5 py-4 shadow-md animate-in fade-in slide-in-from-top-2 duration-500"
                            >
                                <span className="w-9 h-9 rounded-full bg-nsp-yellow text-nsp-green flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-4 h-4" />
                                </span>
                                <div className="text-sm leading-snug">
                                    <b>Bentornato, {welcomeName}!</b>{" "}
                                    <span className="text-white/80">
                                        I tuoi dati sono già salvati: al passaggio 3 ti basterà confermarli.
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] overflow-hidden">
                            {step === 1 && (
                                <Step1
                                    items={items}
                                    setItems={setItems}
                                    onNext={() => setStep(2)}
                                    canGoNext={canGoStep2}
                                />
                            )}
                            {step === 2 && (
                                <Step2
                                    items={items}
                                    updateItem={updateItem}
                                    removeItem={removeItem}
                                    discountCode={discountCode}
                                    setDiscountCode={setDiscountCode}
                                    discountInfo={discountInfo}
                                    setDiscountInfo={setDiscountInfo}
                                    validating={validating}
                                    onValidate={handleValidateDiscount}
                                    subtotal={subtotal}
                                    discountAmount={discountAmount}
                                    vatAmount={vatAmount}
                                    totalWithVat={totalWithVat}
                                    total={total}
                                    onBack={() => setStep(1)}
                                    onNext={() => setStep(3)}
                                    canGoNext={canGoStep3}
                                />
                            )}
                            {step === 3 && (
                                <Step3
                                    customer={customer}
                                    setCustomer={setCustomer}
                                    onBack={() => setStep(2)}
                                    onNext={() => setStep(4)}
                                />
                            )}
                            {step === 4 && (
                                <Step4
                                    items={items}
                                    customer={customer}
                                    subtotal={subtotal}
                                    discountInfo={discountInfo}
                                    discountAmount={discountAmount}
                                    vatAmount={vatAmount}
                                    totalWithVat={totalWithVat}
                                    total={total}
                                    consentContact={consentContact}
                                    setConsentContact={setConsentContact}
                                    consentPrivacy={consentPrivacy}
                                    setConsentPrivacy={setConsentPrivacy}
                                    website={website}
                                    setWebsite={setWebsite}
                                    submitting={submitting}
                                    onBack={() => setStep(3)}
                                    onSubmit={handleSubmit}
                                    canSubmit={canSubmit}
                                />
                            )}
                            {step === 5 && result && (
                                <Step5
                                    result={result}
                                    customerEmail={customer.email}
                                    onReset={() => {
                                        setItems([]);
                                        setStep(1);
                                        setResult(null);
                                        setCustomer({
                                            name: "",
                                            email: "",
                                            phone: "",
                                            company: "",
                                            project: "",
                                        });
                                        setDiscountCode("");
                                        setDiscountInfo(null);
                                        setConsentContact(false);
                                        setConsentPrivacy(false);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============== STEP 1 ==============
const Step1 = ({ items, setItems, onNext, canGoNext }) => {
    const [detail, setDetail] = useState(null);
    const current = detail ? items.find((x) => x.product.id === detail.product.id) || detail : null;
    return (
    <div className="p-5 sm:p-7 lg:p-10">
        <div className="flex items-center gap-3 mb-6">
            <span className="font-display text-nsp-green text-3xl">01</span>
            <h3 className="font-display text-2xl uppercase text-nsp-green tracking-tighter">
                Seleziona le attrezzature
            </h3>
        </div>
        <p className="text-zinc-600 text-sm leading-relaxed">
            Vai al <Link to="/catalogo" className="text-nsp-green font-bold underline">catalogo prodotti</Link> e
            clicca <b>"Aggiungi al preventivo"</b> sulle attrezzature che ti servono.
            Puoi aggiungerne quante vuoi.
        </p>

        {items.length === 0 ? (
            <div className="mt-8 border-2 border-dashed border-zinc-300 p-10 text-center">
                <AlertCircle className="w-10 h-10 mx-auto text-zinc-300 mb-4" />
                <div className="font-bold uppercase tracking-wider text-zinc-500 text-sm">
                    Nessuna attrezzatura selezionata
                </div>
                <div className="text-zinc-400 text-xs mt-2">
                    Aggiungi almeno un mezzo dal catalogo per continuare
                </div>
            </div>
        ) : (
            <ul data-testid="quote-step1-items" className="mt-6 space-y-3">
                {items.map((it) => (
                    <li
                        key={it.product.id}
                        className="flex items-center gap-2 p-4 bg-white border border-nsp-green/25 rounded-2xl shadow-[0_2px_12px_rgba(10,59,37,0.06)]"
                        data-testid={`quote-item-${it.product.id}`}
                    >
                        <button
                            type="button"
                            data-testid={`quote-item-open-${it.product.id}`}
                            onClick={() => setDetail(it)}
                            className="flex items-center gap-4 flex-1 text-left group min-w-0"
                        >
                            <img
                                src={it.product.image || "/nsp-logo.png"}
                                alt={it.product.name}
                                className="w-16 h-16 object-contain bg-white border border-nsp-border rounded-xl p-1 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-nsp-green text-sm uppercase tracking-tight group-hover:underline truncate">
                                    {it.product.name}
                                </div>
                                <div className="text-zinc-500 text-xs">
                                    {formatEuro(it.product.daily)} / giorno · {it.quantity || 1} pz · {it.days || 1} gg
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-nsp-yellow-dark mt-0.5 inline-flex items-center gap-1">
                                    Vedi dettagli <ArrowRight className="w-3 h-3" />
                                </div>
                            </div>
                        </button>
                        <button
                            data-testid={`quote-item-remove-${it.product.id}`}
                            onClick={() =>
                                setItems((p) => p.filter((x) => x.product.id !== it.product.id))
                            }
                            className="p-2 text-zinc-400 hover:text-red-600"
                            aria-label="Rimuovi"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </li>
                ))}
            </ul>
        )}

        <div
            data-testid="quote-add-more-box"
            className="mt-6 border-2 border-dashed border-nsp-green/30 rounded-2xl bg-nsp-green-50 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
        >
            <div className="flex-1">
                <div className="font-bold text-nsp-green text-sm uppercase tracking-tight">
                    {items.length === 0 ? "Aggiungi gli articoli dal catalogo" : "Ti serve altro?"}
                </div>
                <div className="text-zinc-600 text-xs mt-1">
                    {items.length === 0
                        ? "Il tuo preventivo è vuoto: sfoglia il catalogo, scegli le attrezzature e aggiungile con un click."
                        : "Sfoglia il catalogo e aggiungi altre attrezzature al preventivo: le troverai già qui al tuo ritorno."}
                </div>
            </div>
            <Link
                to="/catalogo"
                data-testid="quote-add-more-btn"
                className="rounded-full border-2 border-nsp-green text-nsp-green bg-white px-6 py-3 font-bold uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:bg-nsp-green hover:text-white transition-colors shadow-sm active:scale-95 flex-shrink-0"
            >
                <Plus className="w-4 h-4" />
                {items.length === 0 ? "Sfoglia il catalogo" : "Aggiungi altri prodotti"}
            </Link>
        </div>

        <div className="mt-8 flex justify-end">
            <button
                data-testid="quote-step1-next"
                onClick={onNext}
                disabled={!canGoNext}
                className="rounded-full bg-nsp-green text-white px-8 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-nsp-green-dark disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95 transition-transform"
            >
                Concludi e prosegui <ArrowRight className="w-4 h-4" />
            </button>
        </div>

        {current && (
            <QuoteItemDetail
                item={current}
                onClose={() => setDetail(null)}
                onSave={(days, quantity) => {
                    setItems((p) =>
                        p.map((x) =>
                            x.product.id === current.product.id
                                ? {
                                      ...x,
                                      days,
                                      quantity,
                                      return_date: addDays(new Date(x.pickup_date), days).toISOString(),
                                  }
                                : x
                        )
                    );
                    toast.success(`${current.product.name} aggiornato (${days} gg x${quantity})`);
                    setDetail(null);
                }}
                onRemove={() => {
                    setItems((p) => p.filter((x) => x.product.id !== current.product.id));
                    toast.info(`${current.product.name} rimosso dal preventivo`);
                    setDetail(null);
                }}
            />
        )}
    </div>
    );
};

// ============== STEP 2 ==============
const CalendarModal = ({ open, onClose, title, children, testId }) => {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto px-3 py-5 sm:py-10 animate-in fade-in duration-200"
            onClick={onClose}
            data-testid={testId}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl border border-nsp-green/15 max-w-full animate-in slide-in-from-top-8 fade-in duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between pl-4 pr-2 pt-2.5">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                        {title}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Chiudi"
                        data-testid={`${testId}-close`}
                        className="p-1.5 rounded-full text-zinc-400 hover:text-nsp-green hover:bg-nsp-green-50 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

const DateRangePickerField = ({ pickupValue, returnValue, minDate, onChange, testId, otherPeriods = [], weeklyRate = 0, monthlyRate = 0 }) => {
    const [openPickup, setOpenPickup] = useState(false);
    const [openReturn, setOpenReturn] = useState(false);

    const from = pickupValue ? new Date(pickupValue) : undefined;
    const to = returnValue ? new Date(returnValue) : undefined;

    // Giorni soglia: rientro da qui in poi sblocca la tariffa settimanale/mensile
    const weeklyDate = from && weeklyRate > 0 ? startOfDay(addDays(from, 7)) : null;
    const monthlyDate = from && monthlyRate > 0 ? startOfDay(addDays(from, 30)) : null;

    const sharedModifiers = {
        otherItems: otherPeriods,
        weeklyMark: weeklyDate ? [weeklyDate] : [],
        monthlyMark: monthlyDate ? [monthlyDate] : [],
        rangeMiddle: from && to ? { after: startOfDay(from), before: startOfDay(to) } : [],
        rangeStart: from ? [startOfDay(from)] : [],
        rangeEnd: to ? [startOfDay(to)] : [],
    };
    const sharedModifiersClassNames = {
        otherItems:
            "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-nsp-yellow-dark",
        weeklyMark:
            "relative before:content-[''] before:absolute before:top-0.5 before:left-1/2 before:-translate-x-1/2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-green-600 after:content-['Da_qui_tariffa_settimanale'] after:pointer-events-none after:absolute after:bottom-full after:left-1/2 after:-translate-x-1/2 after:mb-1 after:bg-nsp-green after:text-white after:text-[10px] after:font-bold after:px-2 after:py-1 after:rounded-md after:whitespace-nowrap after:z-50 after:opacity-0 hover:after:opacity-100 focus-visible:after:opacity-100 after:transition-opacity",
        monthlyMark:
            "relative before:content-[''] before:absolute before:top-0.5 before:left-1/2 before:-translate-x-1/2 before:w-1.5 before:h-1.5 before:bg-green-600 after:content-['Da_qui_tariffa_mensile'] after:pointer-events-none after:absolute after:bottom-full after:left-1/2 after:-translate-x-1/2 after:mb-1 after:bg-nsp-green after:text-white after:text-[10px] after:font-bold after:px-2 after:py-1 after:rounded-md after:whitespace-nowrap after:z-50 after:opacity-0 hover:after:opacity-100 focus-visible:after:opacity-100 after:transition-opacity",
        rangeMiddle: "!bg-nsp-green/15 !text-nsp-green !rounded-none",
        rangeStart: "!bg-nsp-green !text-white !rounded-l-md !rounded-r-none hover:!bg-nsp-green",
        rangeEnd: "!bg-nsp-green !text-white !rounded-r-md !rounded-l-none hover:!bg-nsp-green",
    };

    const legend = (
        (otherPeriods.length > 0 || weeklyDate || monthlyDate) && (
            <div className="px-3 pb-3 space-y-2" data-testid="calendar-legend">
                {weeklyDate && (
                    <div
                        data-testid="calendar-weekly-legend"
                        className="flex items-start gap-2.5 text-[13px] leading-snug text-nsp-green bg-nsp-green-50 border border-nsp-green/15 rounded-lg px-3 py-2"
                    >
                        <span className="w-2.5 h-2.5 rounded-full bg-green-600 flex-shrink-0 mt-0.5" />
                        <span>
                            Rientro dal <b>{format(weeklyDate, "d MMM", { locale: itLocale })}</b>:
                            scatta la <b>tariffa settimanale</b> ({formatEuro(weeklyRate)}/sett.)
                        </span>
                    </div>
                )}
                {monthlyDate && (
                    <div
                        data-testid="calendar-monthly-legend"
                        className="flex items-start gap-2.5 text-[13px] leading-snug text-nsp-green bg-nsp-green-50 border border-nsp-green/15 rounded-lg px-3 py-2"
                    >
                        <span className="w-2.5 h-2.5 bg-green-600 flex-shrink-0 mt-0.5" />
                        <span>
                            Rientro dal <b>{format(monthlyDate, "d MMM", { locale: itLocale })}</b>:
                            scatta la <b>tariffa mensile</b> ({formatEuro(monthlyRate)}/mese)
                        </span>
                    </div>
                )}
                {otherPeriods.length > 0 && (
                    <div
                        data-testid="calendar-other-periods-legend"
                        className="flex items-start gap-2.5 text-[13px] leading-snug text-zinc-700 bg-nsp-yellow-50 border border-nsp-yellow-dark/30 rounded-lg px-3 py-2"
                    >
                        <span className="w-2.5 h-2.5 rounded-full bg-nsp-yellow-dark flex-shrink-0 mt-0.5" />
                        Periodo di noleggio degli altri articoli
                    </div>
                )}
            </div>
        )
    );

    const triggerClass =
        "w-full h-11 bg-gray-50 border border-nsp-green/30 rounded-xl px-2.5 sm:px-3 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-nsp-green flex items-center justify-between gap-1.5 hover:border-nsp-green/60 text-left disabled:opacity-60 disabled:cursor-not-allowed";

    const calendarClassNames = {
        caption_label: "text-base font-bold text-nsp-green capitalize",
        nav_button:
            "h-8 w-8 p-0 inline-flex items-center justify-center rounded-lg bg-nsp-green text-nsp-yellow hover:bg-nsp-green-dark transition-colors shadow-md !opacity-100",
    };

    const clearDates = () => {
        onChange({ from: null, to: null });
        setOpenPickup(false);
        setOpenReturn(false);
    };

    const clearButton = (suffix) =>
        from && (
            <div className="px-3 pb-3">
                <button
                    type="button"
                    data-testid={`${testId}-${suffix}`}
                    onClick={clearDates}
                    className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors"
                >
                    <Trash2 className="w-3.5 h-3.5" /> Pulisci date
                </button>
            </div>
        );

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Data di ritiro */}
            <div className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mb-2 block">
                    Giorno di ritiro
                </span>
                <button
                    type="button"
                    data-testid={testId}
                    className={triggerClass}
                    onClick={() => setOpenPickup(true)}
                >
                    <span className={`truncate ${from ? "text-nsp-green font-semibold" : "text-zinc-400"}`}>
                        {from ? format(from, "d MMM yyyy", { locale: itLocale }) : "Seleziona ritiro"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-nsp-green flex-shrink-0" />
                </button>
                <CalendarModal
                    open={openPickup}
                    onClose={() => setOpenPickup(false)}
                    title="Giorno di ritiro"
                    testId={`${testId}-modal`}
                >
                    <Calendar
                        mode="single"
                        selected={from}
                        locale={itLocale}
                        weekStartsOn={1}
                        numberOfMonths={1}
                        showOutsideDays={false}
                        defaultMonth={from || minDate}
                        disabled={(d) => (minDate ? startOfDay(d) < startOfDay(minDate) : false)}
                        classNames={calendarClassNames}
                        modifiers={sharedModifiers}
                        modifiersClassNames={sharedModifiersClassNames}
                        onSelect={(d) => {
                            if (!d) return;
                            const keepTo = to && differenceInCalendarDays(to, d) >= 1 ? to : null;
                            onChange({ from: d, to: keepTo });
                            setOpenPickup(false);
                            if (!keepTo) setTimeout(() => setOpenReturn(true), 250);
                        }}
                        initialFocus
                    />
                    {legend}
                    {clearButton("clear-dates")}
                </CalendarModal>
            </div>

            {/* Data di rientro */}
            <div className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mb-2 block">
                    Giorno di rientro
                </span>
                <button
                    type="button"
                    data-testid={`${testId}-return`}
                    className={triggerClass}
                    disabled={!from}
                    onClick={() => setOpenReturn(true)}
                >
                    <span className={`truncate ${to ? "text-nsp-green font-semibold" : "text-zinc-400"}`}>
                        {to
                            ? format(to, "d MMM yyyy", { locale: itLocale })
                            : from
                            ? "Seleziona rientro"
                            : "Prima il ritiro"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-nsp-green flex-shrink-0" />
                </button>
                <CalendarModal
                    open={openReturn}
                    onClose={() => setOpenReturn(false)}
                    title="Giorno di rientro"
                    testId={`${testId}-return-modal`}
                >
                    <Calendar
                        mode="single"
                        selected={to}
                        locale={itLocale}
                        weekStartsOn={1}
                        numberOfMonths={1}
                        showOutsideDays={false}
                        defaultMonth={to || (from ? addDays(from, 1) : minDate)}
                        disabled={(d) =>
                            from ? differenceInCalendarDays(startOfDay(d), startOfDay(from)) < 1 : true
                        }
                        classNames={calendarClassNames}
                        modifiers={sharedModifiers}
                        modifiersClassNames={sharedModifiersClassNames}
                        onSelect={(d) => {
                            if (!d || !from) return;
                            onChange({ from, to: d });
                            if (weeklyDate && differenceInCalendarDays(d, weeklyDate) === 0) {
                                toast.success("Da questo giorno scatta la tariffa settimanale: hai scelto il rientro più conveniente!");
                            } else if (monthlyDate && differenceInCalendarDays(d, monthlyDate) === 0) {
                                toast.success("Da questo giorno scatta la tariffa mensile: hai scelto il rientro più conveniente!");
                            }
                            setOpenReturn(false);
                        }}
                        initialFocus
                    />
                    {legend}
                    {clearButton("return-clear-dates")}
                </CalendarModal>
            </div>
        </div>
    );
};

const Step2 = ({
    items, updateItem, removeItem,
    discountCode, setDiscountCode, discountInfo, setDiscountInfo,
    validating, onValidate, subtotal, discountAmount, vatAmount, totalWithVat, total,
    onBack, onNext, canGoNext,
}) => {
    const today = startOfDay(new Date());
    const [alignOpen, setAlignOpen] = useState(false);
    const [alignRange, setAlignRange] = useState({ from: null, to: null });
    const [userAligned, setUserAligned] = useState(null);

    // Periodo comune: valorizzato solo se TUTTI gli articoli hanno le stesse date
    const alignedPeriod = (() => {
        if (items.length < 2) return null;
        const first = items[0];
        if (!first.pickup_date || !first.return_date) return null;
        const f = new Date(first.pickup_date);
        const t = new Date(first.return_date);
        const all = items.every(
            (x) =>
                x.pickup_date &&
                x.return_date &&
                differenceInCalendarDays(new Date(x.pickup_date), f) === 0 &&
                differenceInCalendarDays(new Date(x.return_date), t) === 0
        );
        return all ? { from: f, to: t } : null;
    })();

    const confirmAlign = () => {
        const from = new Date(alignRange.from);
        const to = new Date(alignRange.to);
        const d = Math.max(1, differenceInCalendarDays(to, from));
        items.forEach((x) =>
            updateItem(x.product.id, {
                pickup_date: from.toISOString(),
                return_date: to.toISOString(),
                days: d,
            })
        );
        toast.success(
            `Date allineate: ${format(from, "d MMM yyyy", { locale: itLocale })} → ${format(to, "d MMM yyyy", { locale: itLocale })}`
        );
        setUserAligned({ from: from.toISOString(), to: to.toISOString() });
        setAlignOpen(false);
        setAlignRange({ from: null, to: null });
    };

    // Mostra le date sul tasto solo se l'allineamento è stato fatto dall'utente ed è ancora valido
    const showAligned =
        userAligned &&
        alignedPeriod &&
        differenceInCalendarDays(alignedPeriod.from, new Date(userAligned.from)) === 0 &&
        differenceInCalendarDays(alignedPeriod.to, new Date(userAligned.to)) === 0
            ? alignedPeriod
            : null;

    // Tariffe aggregate per i suggerimenti soglia nel picker di allineamento
    const aggWeekly = items.reduce(
        (s, x) => s + ((x.product.weekly || 0) > 0 ? x.product.weekly * (x.quantity || 1) : 0),
        0
    );
    const aggMonthly = items.reduce(
        (s, x) => s + ((x.product.monthly || 0) > 0 ? x.product.monthly * (x.quantity || 1) : 0),
        0
    );

    // Totale e suggerimenti di estensione per il pannello di allineamento
    const alignTotals = (() => {
        if (!alignRange.from || !alignRange.to) return null;
        const days = Math.max(
            1,
            differenceInCalendarDays(new Date(alignRange.to), new Date(alignRange.from))
        );
        const totalFor = (d) =>
            items.reduce(
                (s, x) =>
                    s +
                    computeSmartPrice(
                        d,
                        x.product.daily || 0,
                        x.product.weekly || 0,
                        x.product.monthly || 0
                    ).unit_total * (x.quantity || 1),
                0
            );
        const naiveFor = (d) =>
            items.reduce((s, x) => s + d * (x.product.daily || 0) * (x.quantity || 1), 0);
        const current = totalFor(days);
        const savingsNow = Math.max(0, naiveFor(days) - current);
        const upsells = [];
        const addUpsell = (target, label) => {
            const t = totalFor(target);
            // Risparmio globale = somma dei risparmi di ogni articolo (giornaliero - tariffa smart) alla durata target
            const saving = Math.max(0, naiveFor(target) - t);
            const base = { target, label, total: t, extraDays: target - days, saving };
            if (t <= current) {
                upsells.push({ ...base, extraCost: 0 });
            } else if (t < naiveFor(target)) {
                upsells.push({ ...base, extraCost: t - current });
            }
        };
        if (aggWeekly > 0 && days >= 2 && days < 7) addUpsell(7, "settimanale");
        if (aggMonthly > 0 && days >= 21 && days < 30) addUpsell(30, "mensile");
        return { days, current, savingsNow, upsells };
    })();

    const openAlignPanel = () => {
        setAlignOpen((v) => {
            const next = !v;
            if (next && !alignRange.from) {
                const pre =
                    alignedPeriod ||
                    (userAligned
                        ? { from: new Date(userAligned.from), to: new Date(userAligned.to) }
                        : null);
                if (pre) {
                    setAlignRange({ from: pre.from.toISOString(), to: pre.to.toISOString() });
                }
            }
            return next;
        });
    };

    return (
        <div className="p-5 sm:p-7 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
                <span className="font-display text-nsp-green text-3xl">02</span>
                <h3 className="font-display text-2xl uppercase text-nsp-green tracking-tighter">
                    Date e codice sconto
                </h3>
            </div>
            <div className="flex items-start gap-2 bg-nsp-bg border-l-4 border-nsp-yellow p-3 mb-6 text-xs text-zinc-700">
                <Sparkles className="w-4 h-4 text-nsp-yellow flex-shrink-0 mt-0.5" />
                <span>
                    <b>Periodo di noleggio & calcolo intelligente:</b> seleziona prima il
                    giorno di ritiro e poi il giorno di rientro. Il sistema applicherà
                    automaticamente la combinazione tariffaria più conveniente (mesi +
                    settimane + giorni).
                </span>
            </div>

            {items.length > 1 && (
                <div className="mb-6 -mt-2">
                    <button
                        type="button"
                        data-testid="quote-align-dates"
                        onClick={openAlignPanel}
                        className={`w-full sm:w-auto inline-flex items-center gap-2.5 rounded-2xl border-2 px-5 py-3.5 text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all active:scale-[0.98] text-left ${
                            showAligned
                                ? "border-nsp-green bg-nsp-green-50 text-nsp-green shadow-[0_6px_18px_rgba(10,59,37,0.20)]"
                                : "border-nsp-green/60 bg-white text-nsp-green hover:border-nsp-green shadow-[0_6px_18px_rgba(10,59,37,0.18)]"
                        }`}
                    >
                        {showAligned ? (
                            <>
                                <Check className="w-4 h-4 flex-shrink-0 text-nsp-green-vivid" />
                                <span className="normal-case font-semibold tracking-normal">
                                    Tutte le date di noleggio sono state allineate al periodo{" "}
                                    <b>{format(showAligned.from, "d MMM yyyy", { locale: itLocale })} → {format(showAligned.to, "d MMM yyyy", { locale: itLocale })}</b>
                                </span>
                            </>
                        ) : (
                            <>
                                <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                                Allinea tutte le date di noleggio
                            </>
                        )}
                        <svg
                            className={`w-3.5 h-3.5 ml-auto flex-shrink-0 transition-transform ${alignOpen ? "rotate-180" : ""}`}
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                        >
                            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    {alignOpen && (
                        <div
                            data-testid="quote-align-panel"
                            className="mt-3 bg-white border border-nsp-green/25 rounded-2xl p-4 sm:p-5 shadow-[0_10px_30px_rgba(10,59,37,0.12)] animate-in fade-in slide-in-from-top-2 duration-200"
                        >
                            <p className="text-xs text-zinc-600 mb-4">
                                Scegli il periodo unico: verrà applicato a <b>tutti gli articoli</b> del preventivo.
                            </p>
                            <DateRangePickerField
                                pickupValue={alignRange.from}
                                returnValue={alignRange.to}
                                minDate={today}
                                onChange={(r) =>
                                    setAlignRange({
                                        from: r.from ? new Date(r.from).toISOString() : null,
                                        to: r.to ? new Date(r.to).toISOString() : null,
                                    })
                                }
                                testId="quote-align-period"
                                weeklyRate={aggWeekly}
                                monthlyRate={aggMonthly}
                            />
                            {alignTotals && (
                                <div className="mt-4 space-y-3" data-testid="quote-align-totals">
                                    {/* Costo totale periodo scelto */}
                                    <div className="flex items-center justify-between bg-nsp-green-50 border border-nsp-green/20 rounded-xl px-4 py-3">
                                        <div className="text-xs text-zinc-700">
                                            <b className="text-nsp-green">
                                                {alignTotals.days} {alignTotals.days === 1 ? "giorno" : "giorni"}
                                            </b>{" "}
                                            di noleggio per tutti gli articoli
                                            {alignTotals.savingsNow > 0 && (
                                                <span className="block text-[11px] text-nsp-green mt-0.5">
                                                    Stai già risparmiando <b>{formatEuro(alignTotals.savingsNow)}</b>{" "}
                                                    rispetto alla tariffa giornaliera
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            data-testid="quote-align-total"
                                            className="font-display text-xl text-nsp-green whitespace-nowrap"
                                        >
                                            {formatEuro(alignTotals.current)}
                                        </div>
                                    </div>
                                    {/* Suggerimenti estensione */}
                                    {alignTotals.upsells.map((u) => (
                                        <div
                                            key={u.target}
                                            data-testid={`quote-align-upsell-${u.target}`}
                                            className="flex flex-col sm:flex-row sm:items-center gap-3 bg-nsp-yellow-50 border border-nsp-yellow-dark/40 rounded-xl px-4 py-3 animate-in fade-in duration-200"
                                        >
                                            <div className="flex-1 text-xs text-zinc-800">
                                                <b>
                                                    Aggiungi {u.extraDays} {u.extraDays === 1 ? "giorno" : "giorni"}
                                                </b>{" "}
                                                e passa alla <b>tariffa {u.label}</b>:{" "}
                                                totale <b>{formatEuro(u.total)}</b>
                                                {u.extraCost > 0 && (
                                                    <>
                                                        {" "}(solo <b>+{formatEuro(u.extraCost)}</b> per {u.extraDays}{" "}
                                                        {u.extraDays === 1 ? "giorno" : "giorni"} in più)
                                                    </>
                                                )}
                                                {u.saving > 0 ? (
                                                    <span className="mt-2 flex">
                                                        <span
                                                            data-testid={`quote-align-saving-${u.target}`}
                                                            className="inline-flex items-center gap-1.5 bg-nsp-green text-nsp-yellow font-bold rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs shadow-md"
                                                        >
                                                            <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                                                            Risparmi in totale {formatEuro(u.saving)} su tutti gli
                                                            articoli con più giorni di noleggio!
                                                        </span>
                                                    </span>
                                                ) : (
                                                    u.extraCost === 0 && (
                                                        <span className="mt-2 flex">
                                                            <span className="inline-flex items-center gap-1.5 bg-nsp-green text-nsp-yellow font-bold rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs shadow-md">
                                                                <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                                                                {u.extraDays} {u.extraDays === 1 ? "giorno" : "giorni"} in più senza costi aggiuntivi!
                                                            </span>
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                data-testid={`quote-align-extend-${u.target}`}
                                                onClick={() =>
                                                    setAlignRange((prev) => ({
                                                        from: prev.from,
                                                        to: addDays(new Date(prev.from), u.target).toISOString(),
                                                    }))
                                                }
                                                className="self-start sm:self-auto rounded-full bg-nsp-green text-nsp-yellow px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-nsp-green-dark active:scale-95 transition-transform shadow-md whitespace-nowrap"
                                            >
                                                Estendi a {u.target} giorni
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {alignRange.from && alignRange.to && (
                                <div
                                    data-testid="quote-align-confirm-box"
                                    className="mt-4 bg-nsp-yellow-50 border border-nsp-yellow-dark/30 rounded-xl p-4 animate-in fade-in duration-200"
                                >
                                    <p className="text-sm text-zinc-800 mb-3">
                                        Confermi di allineare <b>tutte le date</b> al periodo{" "}
                                        <b>
                                            {format(new Date(alignRange.from), "d MMM yyyy", { locale: itLocale })} →{" "}
                                            {format(new Date(alignRange.to), "d MMM yyyy", { locale: itLocale })}
                                        </b>
                                        ?
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            data-testid="quote-align-confirm"
                                            onClick={confirmAlign}
                                            className="rounded-full bg-nsp-green text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest hover:bg-nsp-green-dark active:scale-95 transition-transform shadow-md"
                                        >
                                            Sì, allinea tutte le date
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="quote-align-cancel"
                                            onClick={() => {
                                                setAlignRange({ from: null, to: null });
                                                setAlignOpen(false);
                                            }}
                                            className="rounded-full bg-white text-zinc-600 border border-zinc-300 px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-50 active:scale-95 transition-transform"
                                        >
                                            Annulla
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="space-y-5" data-testid="quote-step2-items">
                {items.map((it) => {
                    const pickup = it.pickup_date ? new Date(it.pickup_date) : null;
                    const ret = it.return_date ? new Date(it.return_date) : null;
                    const days = it.days || 1;
                    const pr = computeSmartPrice(
                        days,
                        it.product.daily || 0,
                        it.product.weekly || 0,
                        it.product.monthly || 0
                    );
                    const breakdown = buildBreakdownLabel(
                        pr.months, pr.weeks, pr.extra_days,
                        it.product.daily || 0, it.product.weekly || 0, it.product.monthly || 0
                    );
                    const durationLabel = buildDurationLabel(pr.months, pr.weeks, pr.extra_days);
                    const naive = days * (it.product.daily || 0);
                    const savings = naive > pr.unit_total ? naive - pr.unit_total : 0;

                    const handleRangeChange = (r) => {
                        const newPickup = r.from;
                        if (!newPickup) {
                            updateItem(it.product.id, {
                                pickup_date: null,
                                return_date: null,
                                days: 1,
                            });
                            return;
                        }
                        let newReturn = r.to && differenceInCalendarDays(r.to, r.from) >= 1 ? r.to : null;
                        if (newReturn) {
                            const d = Math.max(1, differenceInCalendarDays(newReturn, newPickup));
                            updateItem(it.product.id, {
                                pickup_date: newPickup.toISOString(),
                                return_date: newReturn.toISOString(),
                                days: d,
                            });
                        } else {
                            updateItem(it.product.id, {
                                pickup_date: newPickup.toISOString(),
                                return_date: null,
                            });
                        }
                    };

                    const otherPeriods = items
                        .filter(
                            (x) =>
                                x.product.id !== it.product.id &&
                                x.pickup_date &&
                                x.return_date
                        )
                        .map((x) => ({
                            from: startOfDay(new Date(x.pickup_date)),
                            to: startOfDay(new Date(x.return_date)),
                        }));

                    // Upsell: a 1-4 giorni dalla soglia settimanale/mensile suggerisce l'estensione
                    let upsell = null;
                    if (pickup && ret) {
                        const check = (target, rateLabel) => {
                            const targetPrice = computeSmartPrice(
                                target,
                                it.product.daily || 0,
                                it.product.weekly || 0,
                                it.product.monthly || 0
                            ).unit_total;
                            const base = {
                                target,
                                extra: target - days,
                                rateLabel,
                                targetTotal: targetPrice * it.quantity,
                            };
                            if (targetPrice <= pr.unit_total) {
                                return {
                                    ...base,
                                    saving: (pr.unit_total - targetPrice) * it.quantity,
                                    extraCost: 0,
                                };
                            }
                            // Costa poco di più: mostra il confronto prezzo (solo se la tariffa è davvero scontata)
                            if (targetPrice < target * (it.product.daily || 0)) {
                                return {
                                    ...base,
                                    saving: 0,
                                    extraCost: (targetPrice - pr.unit_total) * it.quantity,
                                };
                            }
                            return null;
                        };
                        if ((it.product.weekly || 0) > 0 && days >= 3 && days < 7) {
                            upsell = check(7, "settimanale");
                        } else if ((it.product.monthly || 0) > 0 && days >= 26 && days < 30) {
                            upsell = check(30, "mensile");
                        }
                    }

                    return (
                        <div
                            key={it.product.id}
                            className="bg-white border-2 border-nsp-green/40 rounded-2xl shadow-[0_3px_14px_rgba(10,59,37,0.10)]"
                            data-testid={`quote-item-row-${it.product.id}`}
                        >
                            {/* Header prodotto */}
                            <div className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 bg-nsp-green-50/60 border-b border-nsp-green/10 rounded-t-2xl">
                                <img
                                    src={it.product.image || "/nsp-logo.png"}
                                    alt={it.product.name}
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white border border-nsp-green/15 rounded-xl p-1.5 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-nsp-green text-sm sm:text-base uppercase leading-tight line-clamp-2">
                                        {it.product.name}
                                    </div>
                                    <div className="text-zinc-500 text-[11px] sm:text-xs mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                        <span><b className="text-nsp-green">{formatEuro(it.product.daily)}</b>/gg</span>
                                        {it.product.weekly > 0 && (
                                            <span><b className="text-nsp-green">{formatEuro(it.product.weekly)}</b>/sett.</span>
                                        )}
                                        {it.product.monthly > 0 && (
                                            <span><b className="text-nsp-green">{formatEuro(it.product.monthly)}</b>/mese</span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    data-testid={`quote-step2-remove-${it.product.id}`}
                                    onClick={() => removeItem(it.product.id)}
                                    className="p-2 rounded-full text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                    aria-label="Rimuovi"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4 sm:p-5">
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:gap-4">
                                <DateRangePickerField
                                    pickupValue={it.pickup_date}
                                    returnValue={it.return_date}
                                    minDate={today}
                                    onChange={handleRangeChange}
                                    otherPeriods={otherPeriods}
                                    weeklyRate={it.product.weekly || 0}
                                    monthlyRate={it.product.monthly || 0}
                                    testId={`quote-period-${it.product.id}`}
                                />
                                <div className="block sm:w-[170px]">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mb-2 block">
                                        Quantità
                                    </span>
                                    <div className="flex h-11 border border-nsp-green/30 rounded-xl bg-white w-full max-w-[180px] overflow-hidden">
                                        <button
                                            data-testid={`quote-qty-minus-${it.product.id}`}
                                            onClick={() =>
                                                updateItem(it.product.id, {
                                                    quantity: Math.max(1, it.quantity - 1),
                                                })
                                            }
                                            className="w-10 hover:bg-nsp-green-50 flex-shrink-0 transition-colors"
                                        >
                                            <Minus className="w-3 h-3 mx-auto" />
                                        </button>
                                        <div className="flex-1 flex items-center justify-center text-sm font-bold border-x border-nsp-green/15">
                                            {it.quantity}
                                        </div>
                                        <button
                                            data-testid={`quote-qty-plus-${it.product.id}`}
                                            onClick={() =>
                                                updateItem(it.product.id, {
                                                    quantity: Math.min(20, it.quantity + 1),
                                                })
                                            }
                                            className="w-10 hover:bg-nsp-green-50 flex-shrink-0 transition-colors"
                                        >
                                            <Plus className="w-3 h-3 mx-auto" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Smart pricing breakdown */}
                            <div
                                data-testid={`quote-breakdown-${it.product.id}`}
                                className="mt-4 bg-nsp-green-50/70 border border-nsp-green/15 border-l-4 border-l-nsp-green rounded-xl p-3.5 sm:p-4"
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-nsp-green mb-1.5 flex items-center gap-1.5">
                                            <Sparkles className="w-3 h-3" />
                                            Tariffa ottimale applicata
                                        </div>
                                        <div className="text-sm text-nsp-green">
                                            <b>{durationLabel}</b>{" "}
                                            <span className="text-zinc-500">
                                                ({days} {days === 1 ? "giorno" : "giorni"} totali)
                                            </span>
                                        </div>
                                        {pickup && ret && (
                                            <div className="text-xs text-zinc-500 mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                                                <span>
                                                    Uscita:{" "}
                                                    <b className="text-nsp-green">
                                                        {format(pickup, "d MMM yyyy", { locale: itLocale })}
                                                    </b>
                                                </span>
                                                <span>
                                                    Rientro:{" "}
                                                    <b className="text-nsp-green">
                                                        {format(ret, "d MMM yyyy", { locale: itLocale })}
                                                    </b>
                                                </span>
                                            </div>
                                        )}
                                        <div className="text-xs text-zinc-600 mt-1.5 font-mono">
                                            {breakdown}
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                                            Subtotale
                                        </div>
                                        <div className="font-display text-xl text-nsp-green">
                                            {formatEuro(pr.unit_total * it.quantity)}
                                        </div>
                                        {savings > 0 && (
                                            <div
                                                data-testid={`quote-savings-${it.product.id}`}
                                                className="inline-block text-[10px] font-bold text-nsp-green-dark bg-nsp-yellow px-2.5 py-1 mt-1.5 uppercase tracking-wider rounded-full"
                                            >
                                                Risparmi {formatEuro(savings * it.quantity)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Upsell soglia tariffaria */}
                            {upsell && (
                                <div
                                    data-testid={`quote-upsell-${it.product.id}`}
                                    className="mt-3 bg-nsp-yellow-50 border border-nsp-yellow-dark/40 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2.5"
                                >
                                    <div className="flex-1 text-xs text-nsp-green leading-relaxed">
                                        <b>
                                            Aggiungi {upsell.extra} {upsell.extra === 1 ? "giorno" : "giorni"}
                                        </b>{" "}
                                        e passi alla <b>tariffa {upsell.rateLabel}</b>
                                        {upsell.saving > 0 ? (
                                            <>
                                                : <b className="text-nsp-green-vivid">risparmi {formatEuro(upsell.saving)}</b> con più giorni di noleggio!
                                            </>
                                        ) : upsell.extraCost > 0 ? (
                                            <>
                                                : da {formatEuro(pr.unit_total * it.quantity)} a <b>{formatEuro(upsell.targetTotal)}</b> —
                                                solo <b className="text-nsp-green-vivid">{formatEuro(upsell.extraCost)} in più</b> per{" "}
                                                {upsell.extra} {upsell.extra === 1 ? "giorno" : "giorni"} extra di noleggio!
                                            </>
                                        ) : (
                                            <> allo stesso prezzo: più giorni di noleggio gratis!</>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`quote-upsell-apply-${it.product.id}`}
                                        onClick={() => {
                                            updateItem(it.product.id, {
                                                return_date: addDays(pickup, upsell.target).toISOString(),
                                                days: upsell.target,
                                            });
                                            toast.success(
                                                `${it.product.name}: esteso a ${upsell.target} giorni con tariffa ${upsell.rateLabel}`
                                            );
                                        }}
                                        className="rounded-full bg-nsp-green text-nsp-yellow px-4 py-2 font-bold uppercase tracking-widest text-[10px] hover:bg-nsp-green-dark transition-colors shadow-sm active:scale-95 flex-shrink-0"
                                    >
                                        Estendi a {upsell.target} giorni
                                    </button>
                                </div>
                            )}

                            {/* Servizi aggiuntivi per questo articolo */}
                            <ServicesManager
                                services={it.services || []}
                                onChange={(newServices) =>
                                    updateItem(it.product.id, { services: newServices })
                                }
                                itemTestId={`quote-item-${it.product.id}`}
                            />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Discount */}
            <div className="mt-8 bg-nsp-green text-white rounded-2xl p-5 sm:p-7 border-2 border-nsp-green shadow-[0_10px_30px_-12px_rgba(10,59,37,0.5)] relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-nsp-yellow/10 blur-2xl pointer-events-none" />
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-nsp-yellow flex items-center justify-center flex-shrink-0">
                        <Tag className="w-5 h-5 text-nsp-green" strokeWidth={2.4} />
                    </div>
                    <div>
                        <div className="font-bold uppercase tracking-wider text-base">
                            Hai un codice sconto?
                        </div>
                        <div className="text-white/70 text-xs mt-0.5">
                            Inseriscilo qui: lo sconto viene applicato subito al totale.
                        </div>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2.5 mt-4 sm:mt-5">
                    <div className="relative flex-1">
                        <Tag className="w-4 h-4 text-nsp-green/50 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            data-testid="quote-discount-input"
                            type="text"
                            placeholder="Es. BENVENUTO10"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && discountCode.trim() && !validating) onValidate();
                            }}
                            maxLength={30}
                            autoComplete="off"
                            className="w-full h-14 bg-white text-nsp-green pl-11 pr-4 text-base font-bold focus:outline-none focus:ring-2 focus:ring-nsp-yellow uppercase tracking-wider rounded-xl placeholder:text-nsp-green/35"
                        />
                    </div>
                    <button
                        data-testid="quote-discount-validate"
                        onClick={onValidate}
                        disabled={validating || !discountCode.trim()}
                        className={`h-14 px-8 font-bold uppercase tracking-widest text-sm rounded-xl flex items-center justify-center gap-2 transition-all ${
                            validating || !discountCode.trim()
                                ? "bg-white/10 text-white/40 border border-white/15 cursor-not-allowed"
                                : "bg-gradient-to-b from-nsp-yellow to-nsp-yellow-dark text-nsp-green border border-nsp-yellow-dark/50 shadow-[0_6px_16px_-4px_rgba(250,204,21,0.5),inset_0_1px_0_rgba(255,255,255,0.4)] hover:from-white hover:to-white hover:shadow-[0_8px_20px_-4px_rgba(255,255,255,0.35)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                        }`}
                    >
                        {validating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <Check className="w-4 h-4" strokeWidth={3} /> Applica
                            </>
                        )}
                    </button>
                </div>
                {discountInfo?.valid && (
                    <div
                        className="mt-4 bg-white/10 border border-nsp-yellow/40 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm"
                        data-testid="discount-success"
                    >
                        <span className="w-6 h-6 rounded-full bg-nsp-yellow flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-nsp-green" strokeWidth={3} />
                        </span>
                        <span className="text-nsp-yellow font-semibold">{discountInfo.description}</span>
                        <button
                            onClick={() => {
                                setDiscountInfo(null);
                                setDiscountCode("");
                            }}
                            data-testid="discount-remove"
                            className="ml-auto text-xs underline text-white/70 hover:text-white flex-shrink-0"
                        >
                            Rimuovi
                        </button>
                    </div>
                )}
            </div>

            {/* Totals */}
            <div className="mt-6 border border-nsp-border" data-testid="quote-totals">
                <Row label="Subtotale (IVA escl.)" value={formatEuro(subtotal)} />
                {discountAmount > 0 && (
                    <Row label={`Sconto (-${discountInfo.percentage}%)`} value={`- ${formatEuro(discountAmount)}`} accent />
                )}
                <Row label="Imponibile" value={formatEuro(total)} />
                <Row label={`IVA (${VAT_RATE}%)`} value={formatEuro(vatAmount)} />
                <Row label="Totale (IVA incl.)" value={formatEuro(totalWithVat)} highlight />
                {computeTotalSaved(items, discountAmount) > 0 && (
                    <div
                        data-testid="quote-total-saved"
                        className="flex items-center justify-between gap-2 bg-nsp-green px-4 py-3 text-sm font-bold text-nsp-yellow"
                    >
                        <span className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 flex-shrink-0" />
                            Hai risparmiato
                        </span>
                        <span>{formatEuro(computeTotalSaved(items, discountAmount))}</span>
                    </div>
                )}
            </div>

            <div className="mt-8 flex justify-between gap-3">
                <button
                    data-testid="quote-step2-back"
                    onClick={onBack}
                    className="px-6 py-4 font-bold uppercase tracking-widest text-xs text-zinc-600 hover:text-nsp-green flex items-center gap-2"
                >
                    <ArrowLeft className="w-4 h-4" /> Indietro
                </button>
                <button
                    data-testid="quote-step2-next"
                    onClick={onNext}
                    disabled={!canGoNext}
                    className="rounded-full bg-nsp-green text-white px-8 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-nsp-green-dark disabled:opacity-40 shadow-md active:scale-95 transition-transform"
                >
                    Continua <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// ============== STEP 3 ==============
const FIELD_RULES = {
    name: { test: (v) => v.trim().length >= 2, msg: "Inserisci almeno 2 caratteri" },
    email: { test: (v) => /^\S+@\S+\.\S+$/.test(v), msg: "Inserisci un'email valida (es. nome@dominio.it)" },
    phone: { test: (v) => v.replace(/\D/g, "").length >= 5, msg: "Inserisci un numero di telefono valido" },
};

const fieldClass = (state) =>
    `h-12 w-full bg-gray-50 border rounded-xl px-4 pr-10 focus:outline-none focus:ring-2 text-gray-900 transition-colors ${
        state === "valid"
            ? "border-green-500 focus:ring-green-500"
            : state === "invalid"
              ? "border-red-400 focus:ring-red-400"
              : "border-nsp-green/30 focus:ring-nsp-green"
    }`;

const Step3 = ({ customer, setCustomer, onBack, onNext }) => {
    const [touched, setTouched] = useState({});
    const markTouched = (f) => setTouched((p) => ({ ...p, [f]: true }));

    const stateOf = (f) => {
        const v = customer[f] || "";
        if (FIELD_RULES[f].test(v)) return "valid";
        if (touched[f] && v.length > 0) return "invalid";
        if (touched[f]) return "invalid";
        return "neutral";
    };

    const valid = Object.keys(FIELD_RULES).every((f) => FIELD_RULES[f].test(customer[f] || ""));

    const renderStatus = (f) => {
        const s = stateOf(f);
        if (s === "valid") {
            return (
                <Check
                    data-testid={`customer-${f}-valid`}
                    className="w-4 h-4 text-green-600 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    strokeWidth={3}
                />
            );
        }
        return null;
    };

    const renderError = (f) => {
        if (stateOf(f) !== "invalid") return null;
        return (
            <div
                data-testid={`customer-${f}-error`}
                className="flex items-center gap-1.5 text-red-600 text-xs mt-1.5"
            >
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {FIELD_RULES[f].msg}
            </div>
        );
    };

    return (
        <div className="p-5 sm:p-7 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
                <span className="font-display text-nsp-green text-3xl">03</span>
                <h3 className="font-display text-2xl uppercase text-nsp-green tracking-tighter">
                    I tuoi dati di contatto
                </h3>
            </div>
            <p className="text-zinc-600 text-sm">
                Riceverai il PDF del preventivo all'email indicata. Lasciaci anche un
                recapito per ricontattarti.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <Field label="Nome e Cognome *">
                    <div className="relative">
                        <input
                            data-testid="customer-name"
                            type="text"
                            value={customer.name}
                            onChange={(e) =>
                                setCustomer({ ...customer, name: e.target.value })
                            }
                            onBlur={() => markTouched("name")}
                            maxLength={120}
                            autoComplete="name"
                            className={fieldClass(stateOf("name"))}
                        />
                        {renderStatus("name")}
                    </div>
                    {renderError("name")}
                </Field>
                <Field label="Azienda (opzionale)">
                    <input
                        data-testid="customer-company"
                        type="text"
                        value={customer.company}
                        onChange={(e) =>
                            setCustomer({ ...customer, company: e.target.value })
                        }
                        maxLength={200}
                        autoComplete="organization"
                        className="h-12 w-full bg-gray-50 border border-nsp-green/30 rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-nsp-green text-gray-900"
                    />
                </Field>
                <Field label="Email *">
                    <div className="relative">
                        <input
                            data-testid="customer-email"
                            type="email"
                            value={customer.email}
                            onChange={(e) =>
                                setCustomer({ ...customer, email: e.target.value })
                            }
                            onBlur={() => markTouched("email")}
                            maxLength={150}
                            autoComplete="email"
                            className={fieldClass(stateOf("email"))}
                        />
                        {renderStatus("email")}
                    </div>
                    {renderError("email")}
                </Field>
                <Field label="Telefono *">
                    <div className="relative">
                        <input
                            data-testid="customer-phone"
                            type="tel"
                            value={customer.phone}
                            onChange={(e) =>
                                setCustomer({ ...customer, phone: e.target.value })
                            }
                            onBlur={() => markTouched("phone")}
                            maxLength={30}
                            autoComplete="tel"
                            inputMode="tel"
                            className={fieldClass(stateOf("phone"))}
                        />
                        {renderStatus("phone")}
                    </div>
                    {renderError("phone")}
                </Field>
                <Field label="Descrizione progetto / cantiere (opzionale)" full>
                    <textarea
                        data-testid="customer-project"
                        rows={3}
                        value={customer.project}
                        onChange={(e) =>
                            setCustomer({ ...customer, project: e.target.value })
                        }
                        maxLength={1000}
                        placeholder="Es. demolizione di un muro e scavo per fondazione..."
                        className="w-full bg-gray-50 border border-nsp-green/30 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-nsp-green text-gray-900"
                    />
                </Field>
            </div>

            <div className="mt-8 flex justify-between gap-3">
                <button
                    data-testid="quote-step3-back"
                    onClick={onBack}
                    className="px-6 py-4 font-bold uppercase tracking-widest text-xs text-zinc-600 hover:text-nsp-green flex items-center gap-2"
                >
                    <ArrowLeft className="w-4 h-4" /> Indietro
                </button>
                <button
                    data-testid="quote-step3-next"
                    onClick={onNext}
                    disabled={!valid}
                    className="rounded-full bg-nsp-green text-white px-8 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-nsp-green-dark disabled:opacity-40 shadow-md active:scale-95 transition-transform"
                >
                    Continua <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// ============== STEP 4 ==============
const Step4 = ({
    items, customer, subtotal, discountInfo, discountAmount, vatAmount, totalWithVat, total,
    consentContact, setConsentContact, consentPrivacy, setConsentPrivacy,
    website, setWebsite, submitting, onBack, onSubmit, canSubmit,
}) => {
    const totalServices = items.reduce((acc, it) => acc + (it.services?.length || 0), 0);
    return (
    <div className="p-5 sm:p-7 lg:p-10">
        <div className="flex items-center gap-3 mb-6">
            <span className="font-display text-nsp-green text-3xl">04</span>
            <h3 className="font-display text-2xl uppercase text-nsp-green tracking-tighter">
                Conferma e invia
            </h3>
        </div>

        <div className="bg-white border border-nsp-green/25 rounded-2xl shadow-[0_2px_12px_rgba(10,59,37,0.06)] p-5 sm:p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-nsp-green">RIEPILOGO</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <span className="text-zinc-500">Cliente:</span>
                <span className="font-bold text-nsp-green">{customer.name}</span>
                <span className="text-zinc-500">Email:</span>
                <span className="text-nsp-green">{customer.email}</span>
                <span className="text-zinc-500">Telefono:</span>
                <span className="text-nsp-green">{customer.phone}</span>
                <span className="text-zinc-500">Mezzi selezionati:</span>
                <span className="text-nsp-green font-bold">{items.length}</span>
                {totalServices > 0 && (
                    <>
                        <span className="text-zinc-500">Servizi aggiuntivi:</span>
                        <span className="text-nsp-green font-bold">{totalServices}</span>
                    </>
                )}
                <span className="text-zinc-500">Subtotale:</span>
                <span className="text-nsp-green">{formatEuro(subtotal)}</span>
                {discountAmount > 0 && (
                    <>
                        <span className="text-zinc-500">Sconto:</span>
                        <span className="text-nsp-yellow font-bold">- {formatEuro(discountAmount)}</span>
                    </>
                )}
                <span className="text-zinc-500">Imponibile:</span>
                <span className="text-nsp-green">{formatEuro(total)}</span>
                <span className="text-zinc-500">IVA ({VAT_RATE}%):</span>
                <span className="text-nsp-green">{formatEuro(vatAmount)}</span>
                <span className="text-zinc-500 font-bold">Totale (IVA incl.):</span>
                <span className="text-nsp-green font-bold text-base">{formatEuro(totalWithVat)}</span>
                {computeTotalSaved(items, discountAmount) > 0 && (
                    <>
                        <span className="text-nsp-green font-bold flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 flex-shrink-0" /> Hai risparmiato:
                        </span>
                        <span data-testid="quote-recap-saved" className="text-nsp-green font-bold">
                            {formatEuro(computeTotalSaved(items, discountAmount))}
                        </span>
                    </>
                )}
            </div>
        </div>

        {/* Honeypot */}
        <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="absolute opacity-0 pointer-events-none w-0 h-0"
            aria-hidden="true"
        />

        <div className="mt-6 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
                <input
                    data-testid="consent-contact"
                    type="checkbox"
                    checked={consentContact}
                    onChange={(e) => setConsentContact(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-nsp-green"
                />
                <span className="text-sm text-zinc-700 leading-relaxed">
                    Acconsento a essere ricontattato da <b>NSP Nolo Service Point</b> via
                    email, telefono o WhatsApp per discutere i termini del noleggio e del
                    preventivo richiesto.
                </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
                <input
                    data-testid="consent-privacy"
                    type="checkbox"
                    checked={consentPrivacy}
                    onChange={(e) => setConsentPrivacy(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-nsp-green"
                />
                <span className="text-sm text-zinc-700 leading-relaxed">
                    Ho letto e accetto l'<a href="/privacy" target="_blank" rel="noreferrer" className="text-nsp-green underline font-bold">informativa
                    privacy</a> (GDPR). I dati saranno trattati esclusivamente per
                    elaborare il preventivo. *
                </span>
            </label>
        </div>

        <div className="mt-8 flex justify-between gap-3">
            <button
                data-testid="quote-step4-back"
                onClick={onBack}
                className="px-6 py-4 font-bold uppercase tracking-widest text-xs text-zinc-600 hover:text-nsp-green flex items-center gap-2"
            >
                <ArrowLeft className="w-4 h-4" /> Indietro
            </button>
            <button
                data-testid="quote-submit"
                onClick={onSubmit}
                disabled={!canSubmit || submitting}
                className="rounded-full bg-nsp-yellow text-nsp-green px-8 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-nsp-green hover:text-nsp-yellow transition-colors disabled:opacity-40 shadow-md active:scale-95"
            >
                {submitting ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...
                    </>
                ) : (
                    <>
                        <FileText className="w-4 h-4" /> Genera Preventivo
                    </>
                )}
            </button>
        </div>
    </div>
    );
};

// ============== STEP 5: RESULT ==============
const Step5 = ({ result, customerEmail, onReset }) => {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${result.pdf_url}`;
    const previewUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/quote/${result.id}/preview`;
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [pageCount, setPageCount] = useState(1);

    const openViewer = async () => {
        setViewerOpen(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL || ""}/api/quote/${result.id}/pages`
            );
            const data = await res.json();
            if (data.pages) setPageCount(data.pages);
        } catch (_) {}
    };

    useEffect(() => {
        if (!viewerOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && setViewerOpen(false);
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [viewerOpen]);

    return (
        <div className="p-5 sm:p-8 lg:p-12 text-center" data-testid="quote-success">
            <div className="w-20 h-20 mx-auto bg-nsp-green rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-nsp-yellow" strokeWidth={3} />
            </div>
            <h3 className="font-display text-3xl uppercase text-nsp-green mt-6 tracking-tighter">
                Preventivo creato!
            </h3>
            <div className="text-zinc-600 mt-2">
                Numero: <b className="text-nsp-green">{result.quote_number}</b>
            </div>
            <div className="mt-4 inline-block bg-nsp-bg border border-nsp-border px-6 py-3">
                <div className="text-xs uppercase tracking-wider text-zinc-500">Totale (IVA incl.)</div>
                <div className="font-display text-3xl text-nsp-green">
                    {formatEuro(result.total_with_vat ?? result.total)}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                    di cui IVA {formatEuro(result.vat_amount ?? 0)}
                </div>
            </div>

            {(result.rate_savings ?? 0) > 0 && (
                <div
                    data-testid="quote-success-savings"
                    className="mt-4 mx-auto max-w-md bg-nsp-yellow text-nsp-green-dark rounded-2xl px-5 py-3 text-sm font-semibold"
                >
                    ★ Hai risparmiato {formatEuro(result.rate_savings)} rispetto alla tariffa
                    giornaliera, grazie alle tariffe settimanali/mensili applicate automaticamente.
                </div>
            )}

            {result.email_status?.configured && (
                <div className="mt-6 bg-white border border-nsp-green/25 rounded-2xl p-4 text-sm">
                    {result.email_status.customer && (
                        <div className="text-nsp-green flex items-center justify-center gap-2">
                            <Mail className="w-4 h-4" /> Una copia è stata inviata a{" "}
                            <b>{customerEmail}</b>
                        </div>
                    )}
                    {result.email_status.company && (
                        <div className="text-zinc-600 text-xs mt-1">
                            La copia è stata inviata anche all'azienda.
                        </div>
                    )}
                </div>
            )}
            {!result.email_status?.configured && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-2xl text-yellow-900 p-3 text-xs">
                    Servizio email in attivazione. Puoi scaricare il PDF qui sotto.
                </div>
            )}

            {/* Anteprima del preventivo PDF */}
            {!previewError && (
                <div className="mt-8 max-w-md mx-auto" data-testid="quote-preview-box">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                        Anteprima del tuo preventivo
                    </div>
                    <button
                        type="button"
                        data-testid="quote-preview-open"
                        onClick={openViewer}
                        className="relative group w-full rounded-2xl overflow-hidden border-2 border-nsp-green/20 shadow-[0_12px_35px_-10px_rgba(10,59,37,0.35)] bg-white cursor-zoom-in text-left"
                    >
                        {!previewLoaded && (
                            <div className="h-[420px] flex items-center justify-center bg-nsp-bg">
                                <Loader2 className="w-8 h-8 text-nsp-green animate-spin" />
                            </div>
                        )}
                        <img
                            src={previewUrl}
                            alt="Anteprima preventivo PDF"
                            data-testid="quote-preview-image"
                            onLoad={() => setPreviewLoaded(true)}
                            onError={() => setPreviewError(true)}
                            className={`w-full ${previewLoaded ? "block" : "hidden"}`}
                        />
                        {previewLoaded && (
                            <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-nsp-green/90 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md group-hover:bg-nsp-green transition-colors">
                                <ZoomIn className="w-3.5 h-3.5" /> Ingrandisci
                            </span>
                        )}
                    </button>
                    {previewLoaded && (
                        <div className="text-[11px] text-zinc-400 mt-2">
                            Tocca l'anteprima per leggere e sfogliare tutto il preventivo.
                        </div>
                    )}
                </div>
            )}

            {/* Lettore PDF a schermo intero */}
            {viewerOpen && (
                <div
                    data-testid="quote-pdf-viewer"
                    className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex flex-col"
                >
                    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-nsp-green text-white flex-shrink-0">
                        <div className="min-w-0">
                            <div className="font-bold uppercase tracking-wider text-sm truncate">
                                Preventivo {result.quote_number}
                            </div>
                            <div className="text-nsp-yellow text-[10px] uppercase tracking-[0.18em]">
                                {pageCount} {pageCount === 1 ? "pagina" : "pagine"}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <a
                                data-testid="quote-viewer-download"
                                href={pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full bg-nsp-yellow text-nsp-green px-4 py-2 font-bold uppercase tracking-widest text-[11px] flex items-center gap-1.5 hover:bg-white transition-colors active:scale-95"
                            >
                                <Download className="w-3.5 h-3.5" /> Scarica
                            </a>
                            <button
                                type="button"
                                data-testid="quote-viewer-close"
                                onClick={() => setViewerOpen(false)}
                                aria-label="Chiudi lettore"
                                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <div
                        className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setViewerOpen(false);
                        }}
                    >
                        <div className="max-w-3xl mx-auto space-y-4 pb-8">
                            {Array.from({ length: pageCount }, (_, i) => (
                                <div key={i} className="relative">
                                    <img
                                        src={`${previewUrl}?page=${i + 1}`}
                                        alt={`Pagina ${i + 1} del preventivo`}
                                        data-testid={`quote-viewer-page-${i + 1}`}
                                        loading="lazy"
                                        className="w-full bg-white rounded-lg shadow-2xl"
                                    />
                                    <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                        {i + 1} / {pageCount}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                    data-testid="quote-download-pdf"
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-nsp-green text-white px-6 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-nsp-green-dark shadow-md active:scale-95 transition-transform"
                >
                    <Download className="w-4 h-4" /> Scarica PDF
                </a>
                <a
                    data-testid="quote-share-whatsapp"
                    href={`https://wa.me/?text=${encodeURIComponent(
                        `Preventivo noleggio ${result.quote_number} (NSP Nolo Service Point) — Totale ${new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(result.total_with_vat ?? result.total)} (IVA incl.)\nScarica il PDF: ${pdfUrl}`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-[#25D366] text-white px-6 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-[#1ebe5b] shadow-md active:scale-95 transition-transform"
                >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                    </svg>
                    Invia su WhatsApp
                </a>
                <button
                    data-testid="quote-new"
                    onClick={onReset}
                    className="rounded-full bg-white text-nsp-green border-2 border-nsp-green px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-nsp-green hover:text-nsp-yellow transition-colors active:scale-95"
                >
                    Nuovo Preventivo
                </button>
            </div>

            <p className="text-zinc-500 text-xs mt-8">
                Un nostro consulente ti contatterà a breve per finalizzare i dettagli.
            </p>
        </div>
    );
};

const Field = ({ label, children, full }) => (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mb-2 block">
            {label}
        </span>
        {children}
    </label>
);

const computeTotalSaved = (items, discountAmount = 0) =>
    items.reduce((s, x) => {
        const d = x.days || 1;
        const pr = computeSmartPrice(d, x.product.daily || 0, x.product.weekly || 0, x.product.monthly || 0);
        return s + Math.max(0, d * (x.product.daily || 0) - pr.unit_total) * (x.quantity || 1);
    }, 0) + (discountAmount || 0);

const Row = ({ label, value, highlight, accent }) => (
    <div
        className={`flex justify-between px-5 py-3 border-b border-nsp-border last:border-b-0 ${
            highlight ? "bg-nsp-green text-white" : ""
        } ${accent ? "text-nsp-yellow-dark" : ""}`}
    >
        <span className={`text-sm ${highlight ? "font-bold uppercase tracking-wider" : ""}`}>
            {label}
        </span>
        <span className={`font-bold ${highlight ? "text-lg" : "text-sm"}`}>{value}</span>
    </div>
);

export default QuoteCalculator;
